import React, { useEffect, useState, useRef } from 'react';
import { useOrganization } from '../../context/OrganizationContext';
import { collection, query, orderBy, onSnapshot, deleteDoc, doc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { validarArchivoCreativo } from './creativeConstraints';
import { db, functions, storage } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { useTranslation } from 'react-i18next';

// Los límites viven en creativeConstraints.js: CreateCampaign.jsx aplica los mismos y
// las copias en este repo se desincronizan.

export default function CreativeLibrary() {
  const { context } = useOrganization();
  const { t } = useTranslation('adsStudio');
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  // AI generation state
  const [genStep, setGenStep] = useState('idle'); // idle, generating_prompt, reviewing_prompt, generating_image
  const [genPrompt, setGenPrompt] = useState('');
  const [imagePrompt, setImagePrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [genError, setGenError] = useState(null);

  const uid = getAuth().currentUser?.uid;
  const basePath = context.isOrganization
    ? `organizations/${context.orgId}`
    : `clientes/${uid}`;

  useEffect(() => {
    if (!uid) return;
    const q = query(
      collection(db, basePath, 'adsCreatives'),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setCreatives(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [uid, basePath]);

  const handleGeneratePrompt = async () => {
    setGenStep('generating_prompt');
    setGenError(null);
    try {
      const call = httpsCallable(functions, 'generateAdContent', { timeout: 60000 });
      const result = await call({
        organizationId: context.isOrganization ? context.orgId : null,
        prompt: genPrompt,
        platforms: ['meta'],
        language: 'en'
      });
      const meta = result.data?.content?.meta;
      setImagePrompt(meta?.imagePrompt || genPrompt);
      setGenStep('reviewing_prompt');
    } catch (err) {
      setGenError(err.message);
      setGenStep('idle');
    }
  };

  const handleGenerateImage = async () => {
    setGenStep('generating_image');
    setGenError(null);
    try {
      const call = httpsCallable(functions, 'generateAdMedia', { timeout: 120000 });
      await call({
        organizationId: context.isOrganization ? context.orgId : null,
        prompt: imagePrompt,
        aspectRatio
      });
      // Creative appears via onSnapshot
      resetModal();
    } catch (err) {
      setGenError(err.message);
      setGenStep('reviewing_prompt');
    }
  };

  const resetModal = () => {
    setShowModal(false);
    setGenStep('idle');
    setGenPrompt('');
    setImagePrompt('');
    setGenError(null);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validacion = validarArchivoCreativo(file);
    if (!validacion.ok) {
      alert(validacion.motivo === 'tamano'
        ? t('creatives.upload.maxSize')
        : t('creatives.upload.error'));
      e.target.value = '';   // sin esto, reelegir el MISMO archivo no dispara onChange
      return;
    }
    const isImage = validacion.tipo === 'image';

    setUploading(true);
    setUploadProgress(0);

    const entityId = context.isOrganization ? context.orgId : uid;
    const ext = file.name.split('.').pop();
    const storageRef = ref(storage, `ads-creatives/${entityId}/${Date.now()}.${ext}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on('state_changed',
      (snapshot) => {
        setUploadProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
      },
      (err) => {
        console.error('Upload error:', err);
        setUploading(false);
        alert(t('creatives.upload.error'));
      },
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        try {
          const call = httpsCallable(functions, 'uploadCreative');
          await call({
            organizationId: context.isOrganization ? context.orgId : null,
            url,
            type: isImage ? 'image' : 'video',
            metadata: { sizeBytes: file.size }
          });
        } catch (err) {
          console.error('Register creative error:', err);
        }
        setUploading(false);
        setUploadProgress(0);
      }
    );
  };

  const handleDelete = async (creativeId) => {
    if (!window.confirm(t('creatives.actions.deleteConfirm'))) return;
    try {
      await deleteDoc(doc(db, basePath, 'adsCreatives', creativeId));
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>...</div>;
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.3rem' }}>{t('creatives.title')}</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => setShowModal(true)} style={{
            padding: '0.5rem 1rem', backgroundColor: '#1a73e8', color: '#fff',
            border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600'
          }}>
            {t('creatives.generateAI')}
          </button>
          <button onClick={() => fileInputRef.current?.click()} disabled={uploading} style={{
            padding: '0.5rem 1rem', backgroundColor: '#f0f0f0', color: '#333',
            border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem'
          }}>
            {uploading ? `${t('creatives.upload.uploading')} ${uploadProgress}%` : t('creatives.uploadBtn')}
          </button>
          <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.mp4,.mov" onChange={handleFileSelect} style={{ display: 'none' }} />
        </div>
      </div>

      {/* Upload progress */}
      {uploading && (
        <div style={{ marginBottom: '1rem', height: '4px', backgroundColor: '#e0e0e0', borderRadius: '2px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${uploadProgress}%`, backgroundColor: '#1a73e8', transition: 'width 0.3s' }} />
        </div>
      )}

      {/* Empty state */}
      {creatives.length === 0 && (
        <div style={{
          padding: '4rem 2rem', textAlign: 'center', backgroundColor: '#f8f9fa',
          borderRadius: '12px', border: '1px dashed #dee2e6'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎨</div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>{t('creatives.empty.title')}</h3>
          <p style={{ margin: 0, color: '#666', fontSize: '0.95rem' }}>{t('creatives.empty.description')}</p>
        </div>
      )}

      {/* Grid */}
      {creatives.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '1rem'
        }}>
          {creatives.map((c) => (
            <div key={c.id} style={{
              backgroundColor: '#fff', borderRadius: '10px', overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e8e8e8'
            }}>
              <div style={{ position: 'relative', paddingBottom: '100%', backgroundColor: '#f0f0f0' }}>
                {c.type === 'image' ? (
                  <img src={c.url} alt="" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <video src={c.url} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
                <span style={{
                  position: 'absolute', top: '8px', right: '8px',
                  padding: '2px 8px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: '700',
                  backgroundColor: c.source === 'ai' ? '#e8f0fe' : '#f0f0f0',
                  color: c.source === 'ai' ? '#1a73e8' : '#666'
                }}>
                  {t(`creatives.badge.${c.source}`)}
                </span>
              </div>
              <div style={{ padding: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.75rem', color: '#888' }}>
                  {c.createdAt?.toDate?.()?.toLocaleDateString() || ''}
                </span>
                <button onClick={() => handleDelete(c.id)} style={{
                  background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: '0.8rem'
                }}>
                  {t('creatives.actions.delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Generate Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '16px', padding: '2rem',
            maxWidth: '500px', width: '90%', maxHeight: '80vh', overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 1.5rem 0' }}>{t('creatives.generateModal.title')}</h3>

            {genError && (
              <div style={{ padding: '0.75rem', backgroundColor: '#f8d7da', borderRadius: '8px', color: '#721c24', marginBottom: '1rem', fontSize: '0.85rem' }}>
                {genError}
              </div>
            )}

            {genStep === 'idle' && (
              <>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                  {t('creatives.generateModal.prompt')}
                </label>
                <textarea
                  value={genPrompt}
                  onChange={(e) => setGenPrompt(e.target.value)}
                  placeholder={t('creatives.generateModal.promptPlaceholder')}
                  rows={3}
                  style={{
                    width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #ddd',
                    fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box'
                  }}
                />
                <label style={{ display: 'block', margin: '1rem 0 0.5rem 0', fontWeight: '500', fontSize: '0.9rem' }}>
                  {t('creatives.generateModal.aspectRatio')}
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {[['1:1', 'square'], ['9:16', 'portrait'], ['16:9', 'landscape']].map(([ratio, key]) => (
                    <button key={ratio} onClick={() => setAspectRatio(ratio)} style={{
                      flex: 1, padding: '0.5rem', borderRadius: '8px', fontSize: '0.8rem',
                      border: aspectRatio === ratio ? '2px solid #1a73e8' : '1px solid #ddd',
                      backgroundColor: aspectRatio === ratio ? '#e8f0fe' : '#fff',
                      color: aspectRatio === ratio ? '#1a73e8' : '#333', cursor: 'pointer'
                    }}>
                      {t(`creatives.generateModal.${key}`)}
                    </button>
                  ))}
                </div>
              </>
            )}

            {genStep === 'reviewing_prompt' && (
              <>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                  {t('creatives.generateModal.reviewPrompt')}
                </label>
                <textarea
                  value={imagePrompt}
                  onChange={(e) => setImagePrompt(e.target.value)}
                  rows={4}
                  style={{
                    width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #ddd',
                    fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box'
                  }}
                />
              </>
            )}

            {(genStep === 'generating_prompt' || genStep === 'generating_image') && (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏳</div>
                {t('creatives.generateModal.generating')}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
              <button onClick={resetModal} style={{
                padding: '0.5rem 1rem', backgroundColor: '#f0f0f0', color: '#333',
                border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem'
              }}>
                {t('creatives.generateModal.cancel')}
              </button>
              {genStep === 'idle' && (
                <button onClick={handleGeneratePrompt} disabled={!genPrompt.trim()} style={{
                  padding: '0.5rem 1rem', backgroundColor: !genPrompt.trim() ? '#ccc' : '#1a73e8', color: '#fff',
                  border: 'none', borderRadius: '8px', cursor: !genPrompt.trim() ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontWeight: '600'
                }}>
                  {t('creatives.generateModal.generate')}
                </button>
              )}
              {genStep === 'reviewing_prompt' && (
                <button onClick={handleGenerateImage} disabled={!imagePrompt.trim()} style={{
                  padding: '0.5rem 1rem', backgroundColor: !imagePrompt.trim() ? '#ccc' : '#28a745', color: '#fff',
                  border: 'none', borderRadius: '8px', cursor: !imagePrompt.trim() ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontWeight: '600'
                }}>
                  {t('creatives.generateModal.generateImage')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
